import { IonIcon, IonModal } from "@ionic/react";
import { forwardRef } from "react";
import { SignedQueryImage } from "@/components/molecules/signed-query-image";
import { checkIcon } from "@/lib/icons/check.icon";
import { useMyOrganizations } from "@/lib/query/organization/use-my-organizations";
import { useSwitchOrganization } from "@/lib/query/organization/use-switch-organization";
import { queryOptions_signed_key_public_by_slug } from "@/lib/requests/file/file.get-signed-key";
import { cx } from "@/lib/style/cva.config";

export const OrganizationSwitcher = forwardRef<
  HTMLIonModalElement,
  React.ComponentProps<typeof IonModal>
>(function OrganizationSwitcher(props, ref) {
  const { data: organizationsData } = useMyOrganizations();
  const switchOrganization = useSwitchOrganization();

  const organizations = organizationsData?.organizations ?? [];
  const activeOrganizationId = organizationsData?.activeOrganizationId;

  const handleSwitchOrganization = async (organizationId: string) => {
    if (organizationId === activeOrganizationId) {
      return;
    }

    await switchOrganization.mutateAsync(organizationId);
    // Close modal after successful switch
    if (ref && "current" in ref && ref.current) {
      ref.current.dismiss();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only dismiss if clicking the backdrop, not the content
    if (e.target === e.currentTarget) {
      if (ref && "current" in ref && ref.current) {
        ref.current.dismiss();
      }
    }
  };

  return (
    <IonModal ref={ref} className="organization-switcher-modal" {...props}>
      <div className="flex min-h-screen items-end justify-center p-5" onClick={handleBackdropClick}>
        <div className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-lg">
          {/* Title */}
          <div className="mb-2 flex items-center">
            <p className="text-base font-medium text-txt-secondary">Account wisselen</p>
          </div>

          {/* Organizations List */}
          <div className="flex flex-col">
            {organizations.map((org, index) => {
              const isActive = org.id === activeOrganizationId;
              if (!org.id) {
                return null;
              }

              return (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => handleSwitchOrganization(org.id!)}
                  disabled={switchOrganization.isPending}
                  className={cx(
                    "flex w-full items-center justify-between px-2 py-4",
                    index < organizations.length - 1 && "border-border-primary border-b",
                    switchOrganization.isPending && "opacity-50",
                    "transition-opacity active:opacity-70",
                  )}
                >
                  <div className="flex items-center gap-3">
                    {org.slug && (
                      <SignedQueryImage
                        width={48}
                        height={48}
                        alt={org.name ?? ""}
                        className="size-12 rounded-lg object-cover"
                        {...queryOptions_signed_key_public_by_slug([
                          "android-chrome-512x512.png",
                          { slug: org.slug },
                        ])}
                      />
                    )}
                    <p className="text-base font-medium text-txt-primary">{org.name}</p>
                  </div>
                  {isActive && <IonIcon icon={checkIcon} className="text-2xl text-brand-500" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </IonModal>
  );
});
